import { FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui';
import { step4Schema, type Step4Data as FormData } from '@/validations';
import { SocialConnectionsPanel } from '@/components/social';
import api from '@/utils/api';

interface StepSocialConnectionsProps {
    initialData?: { linkedinUrl?: string };
    onNext: (data: FormData) => void;
    onBack?: () => void;
    isSubmitting?: boolean;
}

/**
 * StepSocialConnections - Unified wizard step for all social platform connections
 * Replaces separate LinkedIn and Facebook/Instagram steps
 */
export const StepSocialConnections: FC<StepSocialConnectionsProps> = ({
    initialData,
    onNext,
    onBack,
    isSubmitting = false,
}) => {
    const {
        setValue,
        watch,
        handleSubmit,
    } = useForm<FormData>({
        resolver: zodResolver(step4Schema),
        defaultValues: {
            linkedinUrl: initialData?.linkedinUrl || '',
        },
    });

    const [selectedLinkedInUrn, setSelectedLinkedInUrn] = useState<string | undefined>();
    const linkedinUrlValue = watch('linkedinUrl');

    // Handle OAuth callback - check if user just returned from connection
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const linkedInSuccess = urlParams.get('linkedin_posting_success');
        const facebookSuccess = urlParams.get('facebook_posting_success');

        if (linkedInSuccess === 'true' || facebookSuccess === 'true') {
            // Remove query params from URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    // Handle LinkedIn company page selection
    const handleLinkedInPageSelect = useCallback((urn: string, url: string) => {
        setSelectedLinkedInUrn(urn);
        setValue('linkedinUrl', url, { shouldValidate: true });
    }, [setValue]);

    // Handle form submission
    const handleFormSubmit = async (data: FormData) => {
        // Save LinkedIn company page if selected
        if (selectedLinkedInUrn && linkedinUrlValue) {
            try {
                await api.post('/users/linkedin-company-page', { organizationUrn: selectedLinkedInUrn });
            } catch (error) {
                console.error('Failed to save LinkedIn company page:', error);
            }
        }

        onNext({ linkedinUrl: linkedinUrlValue || data.linkedinUrl || '' });
    };

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
            {/* Header */}
            {onBack && (
                <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                        Social Connections
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5">
                        Connect your social accounts to enable automated posting
                    </p>
                </div>
            )}

            {/* Social Connections Panel */}
            <SocialConnectionsPanel
                showLinkedIn={true}
                showFacebook={true}
                showInstagram={true}
                showLinkedInPageSelector={false}
                onLinkedInPageSelect={handleLinkedInPageSelect}
            />

            {/* Actions */}
            <div className="flex gap-3 pt-6 mt-6 border-t border-slate-200 justify-end">
                {onBack && (
                    <Button type="button" variant="secondary" onClick={onBack}>
                        Back
                    </Button>
                )}
                <Button type="submit" variant="primary" isLoading={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Continue'}
                </Button>
            </div>
        </form>
    );
};
